import { useLogging } from "../components/core/ProfileInsights/context/useLogging";
import { useReloadJobSeekerData } from "../context/ViewJobDataContext";
import { type ResumeWorkflow, useViewjobResumeEvent } from "../util/viewjobResumeEvents";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Upper bound on how long the skeleton stays up before we give up and offer a
 * retry. Resume parse is 2s P95 on the legacy parser and 5-6s P95 on the LLM
 * parser, and both CTAs only report success once the resume is persisted, so
 * this is headroom rather than an expected wait. A rejected refetch fails
 * immediately, so this only covers the "came back but the gate is still up" case.
 */
const HYDRATION_TIMEOUT_MS = 10_000;

export type MatchOverviewHydrationStatus = "idle" | "pending" | "error" | "unparseable";

export interface MatchOverviewHydration {
  status: MatchOverviewHydrationStatus;
  /**
   * Undefined when there is no refetch to run (the legacy REST path), so callers
   * omit the retry affordance rather than offering one that always fails.
   */
  retry?: () => void;
}

/**
 * Drives the in-place hydration of Match Overview after the seeker adds a resume
 * (MJP-880).
 *
 * Both gated CTAs — build and upload — emit `resumeWorkflowCompleted` on success.
 * This hook listens for that, refetches the queries backing the OneGraph model,
 * and reports `"pending"` until the resume gate lifts, which is the observable
 * signal that the new resume landed.
 *
 * Settles on `"unparseable"` instead of `"idle"` when the event reports the file
 * couldn't be read: the upload flow treats a parse failure as a successful upload,
 * so the gate lifts on a resume with no profile data behind it to match against.
 *
 * @param isResumeGated current gate state from `useMatchOverviewResumeGate()`
 */
export function useMatchOverviewHydration(isResumeGated: boolean): MatchOverviewHydration {
  const logger = useLogging();
  const reloadJobSeekerData = useReloadJobSeekerData();
  const [status, setStatus] = useState<MatchOverviewHydrationStatus>("idle");
  // The gate and the panel's content come from two independently resolving
  // queries — `hasResumeContent` from viewjob, `matchComparison` from
  // jobDataResult.
  // Tracking that the refetch settled keeps us from declaring success on the
  // viewjob response alone and rendering stale match data.
  const [refetchSettled, setRefetchSettled] = useState(false);
  const workflowRef = useRef<ResumeWorkflow | null>(null);
  const parsedRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const failHydration = useCallback(
    (reason: string) => {
      clearPendingTimeout();
      setStatus("error");
      logger.logGatedMatchOverviewHydrationCompleted(false, workflowRef.current ?? "", reason);
    },
    [clearPendingTimeout, logger],
  );

  const startHydration = useCallback(() => {
    // The legacy REST path provides no refetch, so there is nothing to wait on —
    // fail now instead of holding a skeleton until the timeout. Still needed even
    // though `retry` is withheld below, because the event path lands here too.
    if (!reloadJobSeekerData) {
      failHydration("no_refetch");
      return;
    }

    clearPendingTimeout();
    setStatus("pending");
    setRefetchSettled(false);

    const timeout = setTimeout(() => failHydration("timeout"), HYDRATION_TIMEOUT_MS);
    timeoutRef.current = timeout;

    // Both handlers ignore an attempt that already settled or was superseded by a
    // retry; `timeoutRef` is cleared on success and reassigned on restart.
    const isCurrentAttempt = () => timeoutRef.current === timeout;

    void reloadJobSeekerData().then(
      () => {
        // Resolution is *not* success on its own — the gate must also come down
        // (see the effect below), so the timeout still covers parse lag. It only
        // records that both queries are back, so success can't be declared on a
        // half-refreshed model.
        if (isCurrentAttempt()) {
          setRefetchSettled(true);
        }
      },
      () => {
        // A refetch that never came back can't lift the gate, so fail now instead
        // of making the seeker wait out the timeout.
        if (isCurrentAttempt()) {
          failHydration("refetch_failed");
        }
      },
    );
  }, [reloadJobSeekerData, clearPendingTimeout, failHydration]);

  useViewjobResumeEvent(
    "resumeWorkflowCompleted",
    useCallback(
      ({ workflow, parsed }: { workflow: ResumeWorkflow; parsed: boolean }) => {
        workflowRef.current = workflow;
        parsedRef.current = parsed;
        startHydration();
      },
      [startHydration],
    ),
  );

  // Success is the gate lifting *and* the refetch having settled — the latter is
  // what guarantees the refreshed matchComparison is in the model too, rather than
  // rendering the panel against pre-resume match data.
  useEffect(() => {
    if (status !== "pending" || isResumeGated || !refetchSettled) {
      return;
    }

    clearPendingTimeout();

    // A resume the backend couldn't read lifts the gate but leaves nothing to
    // match against, so this is a distinct outcome rather than a success.
    if (!parsedRef.current) {
      setStatus("unparseable");
      logger.logGatedMatchOverviewHydrationCompleted(
        false,
        workflowRef.current ?? "",
        "unparseable",
      );
      return;
    }

    setStatus("idle");
    logger.logGatedMatchOverviewHydrationCompleted(true, workflowRef.current ?? "");
  }, [status, isResumeGated, refetchSettled, clearPendingTimeout, logger]);

  useEffect(() => clearPendingTimeout, [clearPendingTimeout]);

  return { status, retry: reloadJobSeekerData ? startHydration : undefined };
}
